import { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { RefreshCw, ChartArea } from "lucide-react";
import { db } from "../firebase";
import { auth } from "../firebase";
import RowTracker from "./rowTracker";
import { useNavbar } from "./DashboardLayout";
import { beginGmailOAuth } from "../utils/gmailOAuth";
import TrackerBoard from "../components/TrackerBoard";
import IconButtonWithTooltip from "../components/IconButtonWithTooltip";
import { HEADER_ICON_BUTTON, TrackerSearch, ViewModeToggle } from "../components/trackerHeader";
import { useLocation, useNavigate } from "react-router-dom";
import SankeyModal from "../components/SankeyModal";
import ApplicationEmailsSheet from "../components/ApplicationEmailsSheet";
import { ApiItem, matchesQuery, toApplications } from "../utils/applicationCards";
import { currentSync, syncGmail, useSyncTimes, useSyncing } from "../utils/gmailSync";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

// "just now", "4 min ago", "3 h ago", "2 d ago"
function timeAgo(ms: number) {
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

export default function Tracker() {
  const [data, setData] = useState<ApiItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const syncing = useSyncing();
  // Application whose emails panel is open
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  // Cards fade in on the first board render only, not every Board/List toggle
  const boardShownRef = useRef(false);
  useEffect(() => {
    if (!loading && viewMode === "columns") boardShownRef.current = true;
  });
  const lastSync = useSyncTimes(auth.currentUser)?.lastSyncAt ?? null;
  const [minuteTick, setNow] = useState(0); // re-render so "x min ago" stays fresh
  const [viewMode, setViewMode] = useState<"columns" | "rows">(
      () => (localStorage.getItem("viewMode") as "columns" | "rows") || "columns"
    );
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [showSankey, setShowSankey] = useState(false);
  const { setNavbarActions } = useNavbar();
  const navigate = useNavigate();
  const location = useLocation();


  

  useEffect(() => {
    localStorage.setItem("viewMode", viewMode);
  }, [viewMode]);

  
  useEffect(() => {
    let stopCards: (() => void) | undefined;
    const unsubscribe = auth.onAuthStateChanged((user) => {
      stopCards?.();
      stopCards = undefined;
      if (user) {
        // Live: cards a sync adds or moves show as they're saved, with no
        // refresh (even when the sync's own request fails part-way)
        stopCards = onSnapshot(
          query(collection(db, "users", user.uid, "emails"), where("archiveId", "==", null)),
          (snapshot) => {
            setData(toApplications(snapshot.docs));
            setLoading(false);
          },
          (e) => {
            setError(e?.message ?? "Failed to load");
            setLoading(false);
          },
        );
      } else {
        // No user logged in, redirect to login
        setLoading(false);
        navigate("/login", { replace: true });
      }
    });

    return () => {
      unsubscribe();
      stopCards?.();
    };
  }, [navigate]);



  const loadEmails = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      setError("Please log in to view your applications");
      setLoading(false);
      return;
    }

    const { collection, getDocs, query, where } = await import("firebase/firestore");
    // Current cycle only: archived cards carry the id of their archive.
    const emailsRef = query(
      collection(db, "users", user.uid, "emails"),
      where("archiveId", "==", null)
    );
    const snapshot = await getDocs(emailsRef);
    
    const emails = toApplications(snapshot.docs);
    
    setData(emails);
  } catch (e: any) {
    setError(e?.message ?? "Failed to load");
  } finally {
    setLoading(false);
  }
};
  const handleSync = async () => {
    setError(null);
    const user = auth.currentUser;
    if (!user) {
      setError("Please log in to sync emails");
      return;
    }
    // The cards reload when it finishes (the effect below)
    const result = await syncGmail(user);
    if (result.ok) return;
    if (result.error === "SESSION_EXPIRED") setError("Your session expired. Please log in again.");
    else if (result.error === "SUBSCRIPTION_REQUIRED") setError("Your plan doesn’t include Gmail sync. See Settings → Plan.");
    else if (result.error === "IN_PROGRESS") setError("Already syncing in another tab. Check back in a minute.");
    else if (result.error === "AUTH_REQUIRED") {
      await beginGmailOAuth({
        user,
        apiBase: API_BASE,
        successRedirect: `${window.location.origin}/dashboard/tracker`,
        errorRedirect: `${window.location.origin}/dashboard/tracker`,
      });
    } else setError(result.message ?? "Failed to sync");
  };

  // Reload the cards after a sync in this tab, from the Sync button or one Home
  // started before you came here. Re-read from Firestore: the sync response
  // leaves out fields the email panel needs (body, messageIds, threadIds).
  useEffect(() => {
    const running = currentSync();
    if (!running) return;
    let live = true;
    running.then((r) => {
      if (live && r.ok) loadEmails();
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing]);

  useEffect(() => {
    const tick = window.setInterval(() => setNow((n) => n + 1), 60000);
    return () => window.clearInterval(tick);
  }, []);

  // Home's to-dos link straight to a card's emails. The state is cleared once
  // used, so going back or refreshing doesn't reopen it.
  const requestedItem = (location.state as { openItem?: string } | null)?.openItem;
  useEffect(() => {
    if (!requestedItem || loading) return;
    if (data.some((it) => it.id === requestedItem)) setOpenItemId(requestedItem);
    navigate(location.pathname, { replace: true, state: null });
  }, [requestedItem, loading, data, navigate, location.pathname]);

  const filteredData = useMemo(
    () => data.filter((it) => matchesQuery(it, searchQuery)),
    [data, searchQuery]
  );

  // Title and tools sit in the top bar, in line with the sidebar toggle.
  const header = (
      <div className="flex w-full min-w-0 items-center justify-between gap-4 font-grotesk">
        <div className="flex min-w-0 items-baseline gap-3">
          <h1 className="text-[22px] font-semibold leading-none tracking-[-.03em] text-melt-text1">Applications</h1>
          <div className="hidden items-center gap-2 whitespace-nowrap font-plex text-xs text-melt-text3 sm:flex">
            <span className="h-[7px] w-[7px] rounded-full bg-[#5E9C7F]" />
            {data.length} tracked
            {lastSync && <> · synced {timeAgo(lastSync)}</>}
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <TrackerSearch onQueryChange={setSearchQuery} />

          <IconButtonWithTooltip label="Sankey chart" onClick={() => setShowSankey(true)} className={HEADER_ICON_BUTTON}>
            <ChartArea size={17} strokeWidth={1.75} />
          </IconButtonWithTooltip>

          <ViewModeToggle value={viewMode} onChange={setViewMode} />

          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex h-9 items-center gap-2 rounded-[9px] border border-melt-hair bg-white px-3.5 text-[13.5px] font-semibold text-melt-text1 transition-colors hover:bg-melt-paper disabled:opacity-60"
          >
            <RefreshCw size={16} strokeWidth={1.75} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Sync"}
          </button>
        </div>
      </div>
  );

  useEffect(() => {
    setNavbarActions(header);
    // `header` is rebuilt every render; these are the values it shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNavbarActions, data.length, lastSync, minuteTick, viewMode, syncing]);

  useEffect(() => () => setNavbarActions(null), [setNavbarActions]);

  return (
    <div
      className={`w-full font-grotesk ${
        viewMode === "columns"
          ? // -mt/-mx + pt/px: room for the board's drop highlight, which overflow-x-hidden would clip
            "md:flex md:w-auto md:min-h-0 md:flex-col md:overflow-x-hidden md:h-[calc(100%_+_2rem_+_6px)] md:-mb-8 md:-mx-1.5 md:-mt-1.5 md:px-1.5 md:pt-1.5"
          : ""
      }`}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {error}
        </div>
      )}

      {/* Board/List switch is instant: a crossfade here fought the layout change (the old view got squashed mid-fade) */}
      <div className={viewMode === "columns" ? "md:flex md:min-h-0 md:flex-1 md:flex-col" : ""}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-melt-text3">Loading...</div>
          </div>
        ) : viewMode === "rows" ? (
          <RowTracker
            data={data}
            onUpdate={loadEmails}
            onOpen={(it) => setOpenItemId(it.id)}
            query={searchQuery}
          />
        ) : (
          <TrackerBoard
            data={filteredData}
            onUpdate={loadEmails}
            onOpen={(it) => setOpenItemId(it.id)}
            animateIn={!boardShownRef.current}
            searching={!!searchQuery.trim()}
          />
        )}
      </div>

      {/* Looked up by id so it shows fresh data after an edit */}
      <ApplicationEmailsSheet
        item={openItemId ? data.find((it) => it.id === openItemId) ?? null : null}
        onClose={() => setOpenItemId(null)}
      />

      <SankeyModal
        isOpen={showSankey}
        onClose={() => setShowSankey(false)}
        apps={data}
      />
    </div>
  );
}
